import {
    Button,
    Input,
    SearchableSelect,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    type SelectOption,
} from '@bemor/web-kit/ui';

import type { RiskRuleField } from '@/api/types';

/**
 * BM-125 (E3): the condition tree of a rule as the engine reads it. A node is a group (all / any / not) or a comparison
 * whose left side reads a field, an aggregate over the buyer's events, a function or another rule; the right side is a
 * literal, another field or a parameter of the rule. Nesting stops at five, as the validator does.
 */
export type ConditionNode = Record<string, unknown>;

export const OPERATORS = [
    '=',
    '!=',
    '>',
    '>=',
    '<',
    '<=',
    'in',
    'not_in',
    'in_list',
    'not_in_list',
    'contains',
    'starts_with',
    'is_null',
] as const;

export const AGGREGATE_FNS = ['count', 'sum', 'count_distinct', 'avg'] as const;
export const AGGREGATE_SOURCES = [
    'attempts',
    'approvals',
    'declines',
    'refunds',
    'chargebacks',
    'fraud_reports',
    'three_ds_failures',
    'otp_failures',
] as const;
export const KEY_KINDS = [
    'email',
    'payment_account',
    'bin',
    'ip',
    'device',
    'account',
    'merchant',
    'store',
] as const;
export const WINDOWS = [
    '1m',
    '5m',
    '10m',
    '30m',
    '1h',
    '6h',
    '24h',
    '7d',
    '30d',
    '180d',
    'all_time',
] as const;
/** function name => number of arguments (the validator's table) */
export const FUNCTIONS: Record<string, number> = {
    ratio: 2,
    distance_km: 2,
    is_new_buyer: 0,
    is_new_device: 0,
    is_new_ip: 0,
    is_new_ip_country: 0,
    is_new_payment_account: 0,
    seconds_since_first_seen: 1,
    contaminated: 2,
    linked_count: 3,
    on_list: 2,
    flagged: 1,
    screening_score: 0,
    sum_with_current: 2,
    is_round_amount: 1,
    language_matches_country: 2,
};
export const MAX_DEPTH = 5;

type Kind = 'all' | 'any' | 'not' | 'field' | 'aggregate' | 'fn' | 'rule';
type Right = 'value' | 'value_field' | 'param';

export function kindOf(node: ConditionNode): Kind {
    if ('all' in node) return 'all';
    if ('any' in node) return 'any';
    if ('not' in node) return 'not';
    if ('aggregate' in node) return 'aggregate';
    if ('fn' in node) return 'fn';
    if ('rule' in node) return 'rule';
    return 'field';
}

function rightOf(node: ConditionNode): Right {
    if ('param' in node) return 'param';
    if ('value_field' in node) return 'value_field';
    return 'value';
}

/** The operators a field's type takes (the validator's rules in the builder's words). */
function operatorsFor(type: string | null): readonly string[] {
    switch (type) {
        case 'number':
        case 'money':
            return ['=', '!=', '>', '>=', '<', '<=', 'in', 'not_in', 'is_null'];
        case 'bool':
            return ['=', 'is_null'];
        case 'string':
            return [
                '=',
                '!=',
                'in',
                'not_in',
                'in_list',
                'not_in_list',
                'contains',
                'starts_with',
                'is_null',
            ];
        case 'country':
            return [
                '=',
                '!=',
                'in',
                'not_in',
                'in_list',
                'not_in_list',
                'is_null',
            ];
        case 'list':
            return ['contains', 'is_null'];
        default:
            return OPERATORS;
    }
}

/** A literal as typed: a list for in / not_in (comma separated), a number when numeric, true / false, else the text. */
export function parseLiteral(text: string, op: string): unknown {
    if (op === 'in' || op === 'not_in')
        return text
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
            .map((v) => (/^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v));
    if (op === 'in_list' || op === 'not_in_list') return text.trim();
    if (text === 'true') return true;
    if (text === 'false') return false;
    if (/^-?\d+(\.\d+)?$/.test(text.trim())) return Number(text);
    return text;
}

export function literalText(value: unknown): string {
    if (Array.isArray(value)) return value.map(String).join(', ');
    if (value === null || value === undefined) return '';
    return String(value);
}

const newNode = (kind: Kind): ConditionNode => {
    switch (kind) {
        case 'all':
            return { all: [] };
        case 'any':
            return { any: [] };
        case 'not':
            return { not: { field: 'attempt', op: '>=', value: 2 } };
        case 'aggregate':
            return {
                aggregate: {
                    fn: 'count',
                    group_by: ['email'],
                    window: '1h',
                    source: 'attempts',
                },
                op: '>=',
                value: 3,
            };
        case 'fn':
            return { fn: 'is_new_buyer', args: [] };
        case 'rule':
            return { rule: '' };
        default:
            return { field: 'attempt', op: '>=', value: 2 };
    }
};

function EnumSelect({
    id,
    value,
    options,
    onChange,
    label,
}: {
    id: string;
    value: string;
    options: readonly string[];
    onChange: (v: string) => void;
    label: string;
}) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger id={id} aria-label={label} className="w-full">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o} value={o}>
                        {o}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

export interface ConditionEditorProps {
    node: ConditionNode;
    onChange: (node: ConditionNode) => void;
    fields: readonly RiskRuleField[];
    /** the rule's declared parameters, for `param` right sides */
    params: readonly string[];
    /** the lists a rule may name with in_list */
    lists: readonly string[];
    /** the keys of the other rules of the chain, for `rule` nodes */
    ruleKeys: readonly string[];
    /** the realtime stage: a pre_auth rule may not read a post-auth field */
    stage: string;
    depth?: number;
    onRemove?: () => void;
    idPrefix?: string;
}

/** One node of the tree: a group lists its children with add / remove; a comparison is one row of selectors. */
export function ConditionEditor({
    node,
    onChange,
    fields,
    params,
    lists,
    ruleKeys,
    stage,
    depth = 0,
    onRemove,
    idPrefix = 'c',
}: ConditionEditorProps) {
    const kind = kindOf(node);
    const setKind = (k: Kind) => onChange(newNode(k));
    const kindPicker = (
        <EnumSelect
            id={`${idPrefix}-kind`}
            label="Condition kind"
            value={kind}
            options={
                depth >= MAX_DEPTH
                    ? ['field', 'aggregate', 'fn', 'rule']
                    : ['all', 'any', 'not', 'field', 'aggregate', 'fn', 'rule']
            }
            onChange={(k) => setKind(k as Kind)}
        />
    );
    const remove = onRemove ? (
        <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label="Remove condition"
            onClick={onRemove}
        >
            ×
        </Button>
    ) : null;

    if (kind === 'all' || kind === 'any') {
        const children = (node[kind] as ConditionNode[]) ?? [];
        const set = (next: ConditionNode[]) =>
            onChange({
                ...(node.label ? { label: node.label } : {}),
                [kind]: next,
            });
        return (
            <div
                className="border-border space-y-2 rounded-md border p-3"
                data-testid="condition-group"
                data-kind={kind}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <div className="w-40">{kindPicker}</div>
                    <span className="text-fg-2 text-sm">
                        {kind === 'all'
                            ? 'every condition below'
                            : 'any condition below'}
                    </span>
                    <Input
                        aria-label="Group label"
                        placeholder="Label (optional)"
                        className="max-w-48"
                        value={String(node.label ?? '')}
                        onChange={(e) =>
                            onChange({
                                ...node,
                                ...(e.target.value
                                    ? { label: e.target.value }
                                    : { label: undefined }),
                            })
                        }
                    />
                    <span className="ml-auto">{remove}</span>
                </div>
                <div className="space-y-2 pl-2 md:pl-4">
                    {children.map((child, i) => (
                        <ConditionEditor
                            key={i}
                            node={child}
                            depth={depth + 1}
                            fields={fields}
                            params={params}
                            lists={lists}
                            ruleKeys={ruleKeys}
                            stage={stage}
                            idPrefix={`${idPrefix}-${i}`}
                            onChange={(n) =>
                                set(children.map((c, j) => (j === i ? n : c)))
                            }
                            onRemove={() =>
                                set(children.filter((_, j) => j !== i))
                            }
                        />
                    ))}
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => set([...children, newNode('field')])}
                >
                    Add condition
                </Button>
            </div>
        );
    }
    if (kind === 'not') {
        return (
            <div
                className="border-border space-y-2 rounded-md border p-3"
                data-testid="condition-group"
                data-kind="not"
            >
                <div className="flex items-center gap-2">
                    <div className="w-40">{kindPicker}</div>
                    <span className="text-fg-2 text-sm">
                        the condition below does not hold
                    </span>
                    <span className="ml-auto">{remove}</span>
                </div>
                <div className="pl-2 md:pl-4">
                    <ConditionEditor
                        node={node.not as ConditionNode}
                        depth={depth + 1}
                        fields={fields}
                        params={params}
                        lists={lists}
                        ruleKeys={ruleKeys}
                        stage={stage}
                        idPrefix={`${idPrefix}-n`}
                        onChange={(n) => onChange({ not: n })}
                    />
                </div>
            </div>
        );
    }
    if (kind === 'rule') {
        return (
            <div
                className="flex flex-wrap items-center gap-2"
                data-testid="condition-rule"
            >
                <div className="w-40">{kindPicker}</div>
                <div className="min-w-48 flex-1">
                    <SearchableSelect
                        id={`${idPrefix}-rule`}
                        label="Rule"
                        placeholder="Choose a rule of the chain"
                        options={ruleKeys.map((k) => ({ value: k, label: k }))}
                        value={String(node.rule ?? '') || null}
                        onChange={(v) => onChange({ rule: v ?? '' })}
                    />
                </div>
                {remove}
            </div>
        );
    }

    // a comparison: the left side, the operator, the right side
    const op = typeof node.op === 'string' ? node.op : null;
    const fieldName = typeof node.field === 'string' ? node.field : null;
    const field = fields.find((f) => f.name === fieldName) ?? null;
    const leftType =
        kind === 'aggregate'
            ? 'number'
            : kind === 'fn'
              ? fnType(String(node.fn ?? ''))
              : (field?.type ?? null);
    const right = rightOf(node);
    const fieldOptions: SelectOption[] = fields
        .filter(
            (f) =>
                stage !== 'pre_auth' ||
                f.stages.includes('pre_auth') ||
                f.name.startsWith('score.'),
        )
        .map((f) => ({ value: f.name, label: `${f.name} (${f.type})` }));
    const setOp = (next: string | null) => {
        const { op: _op, value, value_field, param, ...rest } = node;
        void _op;
        if (next === null) {
            onChange(rest);
            return;
        }
        onChange({
            ...rest,
            op: next,
            ...(next === 'is_null'
                ? {}
                : param !== undefined
                  ? { param }
                  : value_field !== undefined
                    ? { value_field }
                    : {
                          value:
                              next === 'in' || next === 'not_in'
                                  ? Array.isArray(value)
                                      ? value
                                      : []
                                  : next === 'in_list' || next === 'not_in_list'
                                    ? (lists[0] ?? '')
                                    : (value ?? ''),
                      }),
        });
    };
    const setRight = (r: Right) => {
        const { value, value_field, param, ...rest } = node;
        void value;
        void value_field;
        void param;
        onChange({
            ...rest,
            ...(r === 'value'
                ? { value: '' }
                : r === 'param'
                  ? { param: params[0] ?? '' }
                  : { value_field: fields[0]?.name ?? '' }),
        });
    };
    return (
        <div
            className="flex flex-wrap items-center gap-2"
            data-testid="condition-comparison"
            data-kind={kind}
        >
            <div className="w-40">{kindPicker}</div>
            {kind === 'field' ? (
                <div className="min-w-56 flex-1">
                    <SearchableSelect
                        id={`${idPrefix}-field`}
                        testId={`${idPrefix}-field-picker`}
                        label="Field"
                        placeholder="Choose a field"
                        options={fieldOptions}
                        value={fieldName}
                        onChange={(v) => onChange({ ...node, field: v ?? '' })}
                    />
                </div>
            ) : kind === 'aggregate' ? (
                <AggregateEditor
                    idPrefix={idPrefix}
                    spec={(node.aggregate as Record<string, unknown>) ?? {}}
                    onChange={(spec) => onChange({ ...node, aggregate: spec })}
                />
            ) : (
                <FunctionEditor
                    idPrefix={idPrefix}
                    fn={String(node.fn ?? '')}
                    args={Array.isArray(node.args) ? node.args : []}
                    fields={fields}
                    onChange={(fn, args) => onChange({ ...node, fn, args })}
                />
            )}
            {leftType === 'bool' && op === null ? (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setOp('=')}
                >
                    compare
                </Button>
            ) : (
                <div className="w-32">
                    <EnumSelect
                        id={`${idPrefix}-op`}
                        label="Operator"
                        value={op ?? (leftType === 'bool' ? 'is true' : '=')}
                        options={
                            leftType === 'bool'
                                ? ['is true', ...operatorsFor('bool')]
                                : operatorsFor(leftType)
                        }
                        onChange={(v) => setOp(v === 'is true' ? null : v)}
                    />
                </div>
            )}
            {op !== null && op !== 'is_null' ? (
                <>
                    <div className="w-28">
                        <EnumSelect
                            id={`${idPrefix}-right`}
                            label="Right side"
                            value={right}
                            options={
                                op === 'in_list' || op === 'not_in_list'
                                    ? ['value']
                                    : ['value', 'param', 'value_field']
                            }
                            onChange={(v) => setRight(v as Right)}
                        />
                    </div>
                    {right === 'param' ? (
                        <div className="w-40">
                            <EnumSelect
                                id={`${idPrefix}-param`}
                                label="Parameter"
                                value={String(node.param ?? '')}
                                options={params}
                                onChange={(v) =>
                                    onChange({ ...node, param: v })
                                }
                            />
                        </div>
                    ) : right === 'value_field' ? (
                        <div className="min-w-48 flex-1">
                            <SearchableSelect
                                id={`${idPrefix}-vf`}
                                label="Field on the right"
                                options={fieldOptions}
                                value={String(node.value_field ?? '') || null}
                                onChange={(v) =>
                                    onChange({ ...node, value_field: v ?? '' })
                                }
                            />
                        </div>
                    ) : op === 'in_list' || op === 'not_in_list' ? (
                        <div className="w-48">
                            <EnumSelect
                                id={`${idPrefix}-list`}
                                label="List"
                                value={String(node.value ?? '')}
                                options={lists}
                                onChange={(v) =>
                                    onChange({ ...node, value: v })
                                }
                            />
                        </div>
                    ) : (
                        <Input
                            aria-label="Value"
                            className="min-w-32 flex-1"
                            placeholder={
                                op === 'in' || op === 'not_in'
                                    ? 'Values, comma separated'
                                    : 'Value'
                            }
                            value={literalText(node.value)}
                            onChange={(e) =>
                                onChange({
                                    ...node,
                                    value: parseLiteral(e.target.value, op),
                                })
                            }
                        />
                    )}
                </>
            ) : null}
            {remove}
        </div>
    );
}

function fnType(name: string): string {
    return [
        'ratio',
        'distance_km',
        'seconds_since_first_seen',
        'contaminated',
        'linked_count',
        'screening_score',
        'sum_with_current',
    ].includes(name)
        ? 'number'
        : 'bool';
}

function AggregateEditor({
    idPrefix,
    spec,
    onChange,
}: {
    idPrefix: string;
    spec: Record<string, unknown>;
    onChange: (spec: Record<string, unknown>) => void;
}) {
    const fn = String(spec.fn ?? 'count');
    const groupBy = Array.isArray(spec.group_by)
        ? spec.group_by.map(String)
        : [];
    return (
        <div
            className="flex flex-wrap items-center gap-2"
            data-testid="aggregate-editor"
        >
            <div className="w-36">
                <EnumSelect
                    id={`${idPrefix}-afn`}
                    label="Aggregate"
                    value={fn}
                    options={AGGREGATE_FNS}
                    onChange={(v) =>
                        onChange({
                            ...spec,
                            fn: v,
                            ...(v === 'count'
                                ? {}
                                : { field: spec.field ?? 'amount_cents' }),
                        })
                    }
                />
            </div>
            <span className="text-fg-2 text-sm">of</span>
            <div className="w-40">
                <EnumSelect
                    id={`${idPrefix}-src`}
                    label="Source"
                    value={String(spec.source ?? 'attempts')}
                    options={AGGREGATE_SOURCES}
                    onChange={(v) => onChange({ ...spec, source: v })}
                />
            </div>
            <span className="text-fg-2 text-sm">by</span>
            <div className="w-40">
                <EnumSelect
                    id={`${idPrefix}-key`}
                    label="Key"
                    value={groupBy[0] ?? 'email'}
                    options={KEY_KINDS}
                    onChange={(v) => onChange({ ...spec, group_by: [v] })}
                />
            </div>
            <span className="text-fg-2 text-sm">over</span>
            <div className="w-28">
                <EnumSelect
                    id={`${idPrefix}-win`}
                    label="Window"
                    value={String(spec.window ?? '1h')}
                    options={WINDOWS}
                    onChange={(v) => onChange({ ...spec, window: v })}
                />
            </div>
            {fn !== 'count' ? (
                <Input
                    aria-label="Aggregated field"
                    className="w-40"
                    placeholder="amount_cents"
                    value={String(spec.field ?? '')}
                    onChange={(e) =>
                        onChange({ ...spec, field: e.target.value })
                    }
                />
            ) : null}
        </div>
    );
}

function FunctionEditor({
    idPrefix,
    fn,
    args,
    fields,
    onChange,
}: {
    idPrefix: string;
    fn: string;
    args: unknown[];
    fields: readonly RiskRuleField[];
    onChange: (fn: string, args: unknown[]) => void;
}) {
    const arity = FUNCTIONS[fn] ?? 0;
    const argText = (a: unknown) =>
        typeof a === 'object' && a !== null && 'field' in a
            ? `@${String((a as { field: unknown }).field)}`
            : literalText(a);
    const parseArg = (text: string): unknown => {
        const name = text.startsWith('@') ? text.slice(1) : null;
        if (name !== null && fields.some((f) => f.name === name))
            return { field: name };
        return parseLiteral(text, '=');
    };
    return (
        <div
            className="flex flex-wrap items-center gap-2"
            data-testid="function-editor"
        >
            <div className="w-56">
                <EnumSelect
                    id={`${idPrefix}-fn`}
                    label="Function"
                    value={fn || 'is_new_buyer'}
                    options={Object.keys(FUNCTIONS)}
                    onChange={(v) =>
                        onChange(
                            v,
                            Array.from({ length: FUNCTIONS[v] ?? 0 }, () => ''),
                        )
                    }
                />
            </div>
            {Array.from({ length: arity }, (_, i) => (
                <Input
                    key={i}
                    aria-label={`Argument ${i + 1}`}
                    className="w-40"
                    placeholder={`arg ${i + 1} (@field or value)`}
                    value={argText(args[i])}
                    onChange={(e) =>
                        onChange(
                            fn,
                            args.map((a, j) =>
                                j === i ? parseArg(e.target.value) : a,
                            ),
                        )
                    }
                />
            ))}
        </div>
    );
}
